import { useEffect, useMemo, useState } from "react";
import { SignInProviderEnum, UserEnum } from "@/__generated__/graphql";
import { useCountdown } from "@/components/hooks/use-count-down";
import { usePathname } from "@/components/hooks/use-pathname";
import { useRoute } from "@/components/shared/router";
import { Button, View } from "@/components/ui";
import { sendAnalyticData } from "@/lib/monitoring/analytics";
import { PosthogAnalytics } from "@/lib/monitoring/posthog";
import { useAuthState } from "@/store/auth";
import { useClientStore } from "@/store/auth/client";
import { useClientConfirmPhoneNumberStore } from "@/store/auth/clientConfirmPhoneNumber";
import { useClientVerifyPhoneNumberStore } from "@/store/auth/clientVerifyPhoneNumber";
import { useSendPinCodeToPhoneStore } from "@/store/auth/sendPinCodeToPhone";
import { useSignInWithPhoneStore } from "@/store/auth/signInWithPhone";
import { AuthModals, LoginType, SignUpStep } from "@/types/users";

import { toast } from "@luce/ui";

import { useAuth } from "../../auth-provider";
import { OtpModal } from "../../otp-modal";

export function OtpVerificationModal() {
  const { replace } = useRoute();
  const pathname = usePathname();
  const [submitted, setSubmitted] = useState(false);

  const { login } = useAuth();
  const {
    data: { phone, pinCode, otpModalOpen, loginByModal, type, userInfo },
    setPinCode,
    showModal,
    setSignUpStep,
    setUserInfo,
  } = useAuthState();

  const {
    loading: loginLoading,
    fetch: loginByPhone,
    error: loginError,
  } = useSignInWithPhoneStore();

  const {
    loading: confirmPhoneNumberLoading,
    fetch: confirmPhoneNumber,
    error: confirmPhoneNumberError,
  } = useClientConfirmPhoneNumberStore();

  const { fetch: resendSignUpCode, loading: resendSignUpCodeLoading } =
    useClientVerifyPhoneNumberStore();
  const { fetch: resendLoginCode, loading: resendLoginCodeLoading } = useSendPinCodeToPhoneStore();

  const { fetch: getUserInfo } = useClientStore();
  const { count, time, resetCount } = useCountdown(59);

  useEffect(() => {
    if (otpModalOpen) {
      resetCount();
      setPinCode("");
      setSubmitted(false);
    }
  }, [otpModalOpen]);

  const onClose = () => {
    setPinCode("");
    showModal();
  };

  const backToLogin = () => {
    if (loginByModal) {
      showModal(AuthModals.LOGIN);
    } else {
      showModal();
    }
  };

  const backToSignUp = () => {
    if (loginByModal) {
      PosthogAnalytics.track("user_sign_up", { path: pathname });
      showModal(AuthModals.SIGN_UP);
    } else {
      showModal();
    }
  };

  const back = () => {
    showModal();
  };

  const onLoginByPhone = () => {
    loginByPhone(
      {
        requestPayload: {
          input: {
            phone,
            code: pinCode,
            userType: UserEnum.Client,
          },
        },
      },
      { selfHandleError: true },
    ).then((res) => {
      if (res.data?.userInfo) {
        setUserInfo(res.data.userInfo);
        login(res.data.loginArgs);
        onLoginSucceed();
      }
    });
  };

  const onSignUpByPhone = () => {
    confirmPhoneNumber(
      {
        requestPayload: {
          input: {
            phone,
            code: pinCode,
            onSignUp: true,
          },
        },
      },
      { selfHandleError: true },
    ).then((res) => {
      if (res.data?.success) {
        if (loginByModal) showModal(AuthModals.ADDRESS);
        else {
          showModal();
          setSignUpStep(SignUpStep.AddAddress);
        }
      }
    });
  };

  const onConfirmByPhone = () => {
    confirmPhoneNumber({
      requestPayload: {
        input: {
          phone,
          code: pinCode,
        },
      },
    }).then((res) => {
      if (res.data?.success) {
        showModal();
        getUserInfo({
          requestPayload: {
            id: userInfo.id,
          },
        });
        if (type === LoginType.Confirm) return;
        replace({ pageKey: "homepage" });
      }
    });
  };

  const onLoginSucceed = () => {
    sendAnalyticData({
      name: "user_sign_in",
      values: {
        method: SignInProviderEnum.PhoneNumber,
        path: pathname,
      },
    });

    showModal();
  };

  const onResendPinCode = () => {
    if (type === LoginType.Login) {
      resendLoginPinCode();
    } else {
      resendSignUpPinCode();
    }
  };

  const resendLoginPinCode = () => {
    resendLoginCode({
      requestPayload: {
        input: {
          phone,
          enableWhatsappOtp: true,
          userType: UserEnum.Client,
        },
      },
    }).then((res) => {
      if (res.error) return;
      resetCount();
      toast.success("Success resend pin code");
    });
  };

  const resendSignUpPinCode = () => {
    resendSignUpCode({
      requestPayload: {
        input: {
          phone,
          onSignUp: true,
          enableWhatsappOtp: true,
        },
      },
    }).then((res) => {
      if (res.error) {
        if (res.error?.includes("Last pin code is still active")) {
          toast.info(res.error);
        }
        return;
      }
      resetCount();
      toast.success("Success resend pin code");
    });
  };

  const { continueText, backText, onBack, onContinue, loading, error } = useMemo(() => {
    switch (type) {
      case LoginType.Login:
      case LoginType.EmailLogin:
        return {
          continueText: "Sign In",
          backText: "Sign In with another number",
          onContinue: onLoginByPhone,
          onBack: backToLogin,
          loading: loginLoading,
          error: loginError,
        };
      case LoginType.SignUp:
        return {
          continueText: "Sign Up",
          backText: "Back",
          onContinue: onSignUpByPhone,
          onBack: backToSignUp,
          loading: confirmPhoneNumberLoading,
          error: confirmPhoneNumberError,
        };
      case LoginType.Confirm:
        return {
          continueText: "Confirm",
          backText: "Back",
          onContinue: onConfirmByPhone,
          onBack: back,
          loading: confirmPhoneNumberLoading,
          error: confirmPhoneNumberError,
        };
      default:
        return {
          continueText: "Continue",
          backText: "Back",
          onContinue: onConfirmByPhone,
          onBack: back,
          loading: confirmPhoneNumberLoading,
          error: confirmPhoneNumberError,
        };
    }
  }, [
    type,
    pinCode,
    loginByModal,
    loginLoading,
    loginError,
    confirmPhoneNumberLoading,
    confirmPhoneNumberError,
  ]);

  return (
    <OtpModal
      resetLoading={resendLoginCodeLoading || resendSignUpCodeLoading}
      open={otpModalOpen}
      onClose={onClose}
      pinCode={pinCode}
      setPinCode={setPinCode}
      onResendPinCode={onResendPinCode}
      phoneNumber={phone}
      time={time}
      count={count}
      errorMessage={submitted && error ? error : ""}
      buttonRender={
        <View className="flex flex-col gap-2">
          <Button
            variant="primary"
            size="lg"
            color="CTA"
            rounded="full"
            loading={loading}
            disabled={pinCode.length !== 6}
            onClick={() => {
              setSubmitted(true);
              onContinue();
            }}
            children={continueText}
          />
          <Button
            variant="secondary"
            size="lg"
            color="CTA2"
            rounded="full"
            onClick={onBack}
            children={backText}
          />
        </View>
      }
    />
  );
}
